// =============================================================================
// faviconBuilder — komplet faviconow wyliczany z logo SVG.
//
// ZERO dodatkowych wywolan AI: skoro logo to prawdziwy wektor, wszystkie rozmiary
// rasteryzujemy lokalnie w przegladarce (canvas). Efekt: pelna spojnosc z logo
// i zero kosztu przy kazdym ponownym pobraniu.
//
// Czyste funkcje (buildIco / buildWebmanifest / buildHeadSnippet) sa testowalne
// w node; rasteryzacja wymaga DOM i zyje tylko w przegladarce.
// =============================================================================

import { normalizeSvgForRaster, svgToDataUrl } from "./svgUtils";
import type { ZipEntry } from "./zipStore";

export const FAVICON_ICO_SIZES = [16, 32, 48];
export const DEFAULT_THEME_COLOR = "#0F172A";

export interface IcoImage {
  size: number;
  png: Uint8Array;
}

// =============================================================================
// Czyste funkcje
// =============================================================================

/**
 * Sklada plik .ico z gotowych PNG-ow.
 * Struktura: ICONDIR (6 B) + ICONDIRENTRY (16 B na obrazek) + surowe PNG.
 * ICO od czasow Visty przyjmuje PNG w srodku — nie musimy pisac BMP.
 */
export function buildIco(images: IcoImage[]): Uint8Array {
  const count = images.length;
  const headerSize = 6 + count * 16;
  const total = headerSize + images.reduce((s, i) => s + i.png.length, 0);

  const buf = new Uint8Array(total);
  const view = new DataView(buf.buffer);

  view.setUint16(0, 0, true); // reserved
  view.setUint16(2, 1, true); // type: 1 = ikona
  view.setUint16(4, count, true);

  let offset = headerSize;
  images.forEach((img, i) => {
    const e = 6 + i * 16;
    buf[e] = img.size >= 256 ? 0 : img.size; // 0 oznacza 256
    buf[e + 1] = img.size >= 256 ? 0 : img.size;
    buf[e + 2] = 0; // liczba kolorow palety
    buf[e + 3] = 0; // reserved
    view.setUint16(e + 4, 1, true); // color planes
    view.setUint16(e + 6, 32, true); // bity na piksel
    view.setUint32(e + 8, img.png.length, true);
    view.setUint32(e + 12, offset, true);
    buf.set(img.png, offset);
    offset += img.png.length;
  });

  return buf;
}

export function buildWebmanifest(themeColor: string = DEFAULT_THEME_COLOR): string {
  return JSON.stringify(
    {
      icons: [
        { src: "/icon-192.png", sizes: "192x192", type: "image/png" },
        { src: "/icon-512.png", sizes: "512x512", type: "image/png" },
      ],
      theme_color: themeColor,
      background_color: "#ffffff",
      display: "browser",
    },
    null,
    2,
  );
}

export function buildHeadSnippet(themeColor: string = DEFAULT_THEME_COLOR): string {
  return [
    `<link rel="icon" href="/favicon.ico" sizes="48x48">`,
    `<link rel="icon" href="/favicon.svg" type="image/svg+xml">`,
    `<link rel="apple-touch-icon" href="/apple-touch-icon.png">`,
    `<link rel="manifest" href="/site.webmanifest">`,
    `<meta name="theme-color" content="${themeColor}">`,
  ].join("\n");
}

/** #abc / abc / #aabbcc -> #aabbcc; smiec -> kolor domyslny. */
export function normalizeHexColor(input: string | null | undefined): string {
  const raw = (input || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`.toLowerCase();
  }
  if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw.toLowerCase()}`;
  return DEFAULT_THEME_COLOR;
}

// =============================================================================
// Rasteryzacja (DOM)
// =============================================================================

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Nie udalo sie wczytac SVG do rasteryzacji"));
    img.src = src;
  });
}

async function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/png"));
  if (!blob) throw new Error("Rasteryzacja do PNG nie powiodla sie");
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * SVG -> PNG o zadanym boku.
 * `opaqueBackground` uzywamy dla apple-touch-icon (iOS nie lubi przezroczystosci),
 * `padding` to margines wewnetrzny w ulamku boku.
 */
export async function rasterizeSvg(
  svg: string,
  size: number,
  opts: { opaqueBackground?: string; padding?: number } = {},
): Promise<Uint8Array> {
  const img = await loadImage(svgToDataUrl(normalizeSvgForRaster(svg, size)));

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Brak kontekstu canvas");

  if (opts.opaqueBackground) {
    ctx.fillStyle = opts.opaqueBackground;
    ctx.fillRect(0, 0, size, size);
  }

  const pad = Math.round(size * (opts.padding ?? 0));
  const inner = size - pad * 2;
  ctx.drawImage(img, pad, pad, inner, inner);

  return canvasToPngBytes(canvas);
}

/** Bajty -> data: URL (podglad w UI; blob: odpada przez CSP img-src). */
export function bytesToDataUrl(bytes: Uint8Array, mime: string): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return `data:${mime};base64,${btoa(binary)}`;
}

/**
 * Pelna paczka: logo + wszystkie favicony + manifest + snippet do <head>.
 * Zwraca liste plikow gotowa do spakowania przez createZip() albo pobrania pojedynczo.
 */
export async function buildFaviconBundle(
  svg: string,
  themeColorInput?: string | null,
): Promise<ZipEntry[]> {
  const themeColor = normalizeHexColor(themeColorInput);
  const encoder = new TextEncoder();
  const files: ZipEntry[] = [];

  // Wektory — skaluja sie same
  files.push({ name: "logo.svg", bytes: encoder.encode(svg) });
  files.push({ name: "favicon.svg", bytes: encoder.encode(svg) });

  // Klasyczne favicony + .ico
  const icoImages: IcoImage[] = [];
  for (const size of FAVICON_ICO_SIZES) {
    const png = await rasterizeSvg(svg, size);
    icoImages.push({ size, png });
    files.push({ name: `favicon-${size}x${size}.png`, bytes: png });
  }
  files.push({ name: "favicon.ico", bytes: buildIco(icoImages) });

  // iOS — bez alfy, z oddechem dookola symbolu
  files.push({
    name: "apple-touch-icon.png",
    bytes: await rasterizeSvg(svg, 180, { opaqueBackground: themeColor, padding: 0.12 }),
  });

  // PWA
  files.push({ name: "icon-192.png", bytes: await rasterizeSvg(svg, 192) });
  files.push({ name: "icon-512.png", bytes: await rasterizeSvg(svg, 512) });
  files.push({ name: "site.webmanifest", bytes: encoder.encode(buildWebmanifest(themeColor)) });
  files.push({ name: "head-snippet.html", bytes: encoder.encode(buildHeadSnippet(themeColor)) });

  return files;
}

/** Wymusza pobranie pliku (blob + <a download> — img-src z CSP tego nie dotyczy). */
export function downloadBytes(bytes: Uint8Array, fileName: string, mime: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
